'use client';

import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LucideIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from "@/lib/utils";

export interface SubMenuItem {
  id: string;
  labelKey: string;
  path: string;
  icon?: LucideIcon;
}

interface SubMenuNavigationProps {
  items: SubMenuItem[];
  activeId: string;
  onItemClick: (item: SubMenuItem) => void;
}

/**
 * Reusable submenu navigation component
 * Displays tabs for sub-navigation with active state
 */
const SubMenuNavigation = ({ items, activeId, onItemClick }: SubMenuNavigationProps) => {
  const { t } = useTranslation();

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="relative flex items-center overflow-hidden mb-4">
      <div
        className="flex items-center gap-1 overflow-x-auto no-scrollbar flex-1 min-w-0 mask-fade-x snap-x snap-mandatory md:snap-none"
      >
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = item.id === activeId;
          return (
            <button
              key={item.id}
              onClick={() => onItemClick(item)}
              className={cn(
                'flex h-9 snap-start items-center gap-1.5 whitespace-nowrap rounded-md border-2 px-3 text-sm font-semibold transition-all flex-shrink-0 md:h-7 md:px-2.5 md:text-xs',
                isActive
                  ? 'bg-primary/15 text-primary primary/40 shadow-sm'
                  : 'text-muted-foreground hover:text-primary hover:bg-primary/10 hover:border-primary/30 border-transparent',
              )}
            >
              {Icon && <Icon className="h-3.5 w-3.5" />}
              <span>{t(item.labelKey)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SubMenuNavigation;
